import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Plus, RefreshCw, Send, XCircle } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Button,
  Card,
  CardBody,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  Modal,
  Select,
  SkeletonText,
  StatusPill,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  Textarea,
  TR,
} from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import {
  useCancelNotification,
  useCreateNotification,
  useHomeBlocks,
  useNotifications,
  useSendNotificationNow,
} from "@/lib/queries";
import { NOTIFICATION_STATUS_META } from "@/lib/utils/statusMeta";
import { formatDateTime, formatRelative } from "@/lib/utils/format";
import { fromDatetimeLocalValue } from "@/lib/utils/datetimeLocal";
import { isNotificationProcessing, isNotificationStalled } from "@/lib/utils/notificationProcessing";
import type { HomeBlock, NotificationCampaign, NotificationCreateInput, NotificationTargetType } from "@/lib/api";

export function NotificationsPage() {
  const notificationsQuery = useNotifications();
  const blocksQuery = useHomeBlocks();
  const createNotification = useCreateNotification();
  const sendNow = useSendNotificationNow();
  const cancelNotification = useCancelNotification();
  const { toast } = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [prefill, setPrefill] = useState<{ targetType: NotificationTargetType; targetId: string; title: string } | null>(null);
  const [sendTarget, setSendTarget] = useState<NotificationCampaign | null>(null);
  const [cancelTarget, setCancelTarget] = useState<NotificationCampaign | null>(null);

  const notifications = notificationsQuery.data ?? [];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("targetType") === "home_block" && params.get("targetId")) {
      setPrefill({
        targetType: "home_block",
        targetId: params.get("targetId") ?? "",
        title: params.get("title") ?? "",
      });
      setFormOpen(true);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  async function handleCreate(payload: NotificationCreateInput) {
    const created = await createNotification.mutateAsync(payload);
    toast({
      title: payload.scheduledAt ? "Notificacion programada" : notificationToastTitle(created),
      variant: created.status === "failed" ? "error" : created.status === "partial" ? "info" : "success",
    });
  }

  async function handleSendNow() {
    if (!sendTarget) return;
    const result = await sendNow.mutateAsync(sendTarget.id);
    toast({
      title: notificationToastTitle(result),
      variant: result.status === "failed" ? "error" : result.status === "partial" ? "info" : "success",
    });
    setSendTarget(null);
  }

  async function handleCancel() {
    if (!cancelTarget) return;
    await cancelNotification.mutateAsync(cancelTarget.id);
    toast({ title: "Notificacion cancelada", variant: "info" });
  }

  return (
    <div>
      <PageHeader
        title="Notificaciones"
        description="Campanas inmediatas y programadas para IESAPP."
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              icon={<RefreshCw className="h-4 w-4" aria-hidden="true" />}
              onClick={() => void notificationsQuery.refetch()}
              loading={notificationsQuery.isFetching && !notificationsQuery.isLoading}
            >
              Actualizar
            </Button>
            <Button icon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={() => setFormOpen(true)}>
              Nueva notificacion
            </Button>
          </div>
        }
      />

      <Card>
        <CardBody className="p-0">
          {notificationsQuery.isLoading && (
            <div className="p-5">
              <SkeletonText lines={5} />
            </div>
          )}
          {notificationsQuery.isError && (
            <div className="p-5">
              <ErrorState description="No se pudieron cargar las notificaciones." onRetry={() => notificationsQuery.refetch()} />
            </div>
          )}
          {notificationsQuery.isSuccess && notifications.length === 0 && (
            <EmptyState
              title="Sin notificaciones todavia"
              description="Crea tu primera campana inmediata o programada."
            />
          )}
          {notifications.length > 0 && (
            <>
              <TableContainer className="hidden rounded-none border-0 sm:block">
                <Table>
                  <THead>
                    <tr>
                      <TH>Titulo</TH>
                      <TH>Estado</TH>
                      <TH>Programada</TH>
                      <TH>Enviada</TH>
                      <TH className="text-right">Acciones</TH>
                    </tr>
                  </THead>
                  <TBody>
                    {notifications.map((n) => (
                      <TR key={n.id}>
                        <TD>
                          <p className="font-medium text-ink">{n.title}</p>
                          <p className="max-w-xs truncate text-xs text-ink-faint">{n.body}</p>
                          {n.deliveries && n.deliveries.length > 0 && (
                            <p className="mt-1 max-w-xs truncate text-xs text-ink-faint">
                              {n.deliveries.slice(0, 4).map((item) => `${formatDateTime(item.scheduledAt)} ${NOTIFICATION_STATUS_META[item.status].label.toLowerCase()}`).join(" · ")}
                            </p>
                          )}
                        </TD>
                        <TD>
                          <NotificationStatusCell n={n} />
                        </TD>
                        <TD>{formatDateTime(n.scheduledAt)}</TD>
                        <TD>{formatDateTime(n.sentAt)}</TD>
                        <TD>
                          <div className="flex justify-end gap-1.5">
                            <NotificationRowActions
                              n={n}
                              onSend={() => setSendTarget(n)}
                              onCancel={() => setCancelTarget(n)}
                            />
                          </div>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableContainer>

              <ul className="divide-y divide-ink/5 sm:hidden">
                {notifications.map((n) => (
                  <li key={n.id} className="space-y-2.5 p-4">
                    <div>
                      <p className="text-sm font-semibold text-ink">{n.title}</p>
                      <p className="mt-0.5 line-clamp-2 text-xs text-ink-faint">{n.body}</p>
                      {n.deliveries && n.deliveries.length > 0 && (
                        <p className="mt-1 line-clamp-2 text-xs text-ink-faint">
                          {n.deliveries.slice(0, 3).map((item) => `${formatDateTime(item.scheduledAt)} ${NOTIFICATION_STATUS_META[item.status].label.toLowerCase()}`).join(" · ")}
                        </p>
                      )}
                    </div>
                    <NotificationStatusCell n={n} />
                    <dl className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <dt className="text-ink-faint">Programada</dt>
                        <dd className="text-ink">{formatDateTime(n.scheduledAt)}</dd>
                      </div>
                      <div>
                        <dt className="text-ink-faint">Enviada</dt>
                        <dd className="text-ink">{formatDateTime(n.sentAt)}</dd>
                      </div>
                    </dl>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <NotificationRowActions
                        n={n}
                        onSend={() => setSendTarget(n)}
                        onCancel={() => setCancelTarget(n)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </CardBody>
      </Card>

      <NotificationFormModal
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setPrefill(null);
        }}
        onSubmit={handleCreate}
        blocks={blocksQuery.data ?? []}
        prefill={prefill}
      />

      <ConfirmDialog
        open={sendTarget !== null}
        onClose={() => setSendTarget(null)}
        onConfirm={handleSendNow}
        title="Enviar notificacion ahora"
        description={sendTarget ? `Se enviara "${sendTarget.title}" de inmediato a la audiencia configurada.` : undefined}
        confirmLabel="Enviar ahora"
      />

      <ConfirmDialog
        open={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancel}
        title="Cancelar notificacion"
        description={cancelTarget ? `Se cancelara el envio programado de "${cancelTarget.title}".` : undefined}
        confirmLabel="Cancelar envio"
        variant="danger"
      />
    </div>
  );
}

function NotificationStatusCell({ n }: { n: NotificationCampaign }) {
  const meta = NOTIFICATION_STATUS_META[n.status];

  if (isNotificationProcessing(n)) {
    if (isNotificationStalled(n)) {
      return (
        <div className="space-y-1">
          <StatusPill tone="danger">Sin confirmacion</StatusPill>
          <p className="max-w-[16rem] text-xs text-ink-faint">
            El job sigue marcado como {n.status} despues del tiempo esperado. Usa
            &quot;Actualizar&quot; para comprobar el estado real del backend.
          </p>
        </div>
      );
    }
    return (
      <div className="space-y-1">
        <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
        <p className="text-xs text-ink-faint">Actualizado {formatRelative(n.updatedAt)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
      {n.status === "failed" && n.errorMessage && (
        <p className="max-w-[16rem] text-xs text-danger-600">{n.errorMessage}</p>
      )}
      <RecipientsSummary n={n} />
    </div>
  );
}

function RecipientsSummary({ n }: { n: NotificationCampaign }) {
  if (n.recipients == null && n.accepted == null && n.failed == null) {
    return null;
  }
  const parts = [
    n.accepted != null && `${n.accepted} aceptados`,
    n.failed ? `${n.failed} fallidos` : null,
    n.recipients != null && `de ${n.recipients}`,
  ].filter(Boolean);
  return <p className="text-xs text-ink-faint">{parts.join(" ")}</p>;
}

function previewOccurrences(
  schedule: "now" | "once" | "daily" | "weekdays" | "weekly",
  dateFrom: string,
  dateTo: string,
  sendTime: string,
  weekdays: number[],
): { count: number; items: string[] } | null {
  if (schedule === "now" || schedule === "once" || !dateFrom || !dateTo || !sendTime) return null;
  const start = new Date(`${dateFrom}T00:00:00`);
  const end = new Date(`${dateTo}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return null;
  const items: string[] = [];
  let count = 0;
  const weeklyDay = start.getDay() === 0 ? 6 : start.getDay() - 1;
  for (const cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
    const day = cursor.getDay() === 0 ? 6 : cursor.getDay() - 1;
    const include = schedule === "daily" || (schedule === "weekdays" && weekdays.includes(day)) || (schedule === "weekly" && day === weeklyDay);
    if (!include) continue;
    count += 1;
    if (items.length < 6) {
      items.push(`${cursor.toLocaleDateString("es-ES", { day: "2-digit", month: "short" })} ${sendTime}`);
    }
  }
  return { count, items };
}

function notificationToastTitle(notification: NotificationCampaign): string {
  if (notification.status === "sent") return "Notificacion enviada";
  if (notification.status === "partial") return "Notificacion enviada parcialmente";
  if (notification.status === "failed") return "Notificacion fallida";
  return "Notificacion creada";
}

function NotificationRowActions({
  n,
  onSend,
  onCancel,
}: {
  n: NotificationCampaign;
  onSend: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      {n.kind !== "campaign" && (n.status === "draft" || n.status === "scheduled") && (
        <Button
          variant="outline"
          size="sm"
          icon={<Send className="h-3.5 w-3.5" aria-hidden="true" />}
          onClick={onSend}
        >
          Enviar ahora
        </Button>
      )}
      {n.status === "scheduled" && (
        <Button
          variant="ghost"
          size="sm"
          icon={<XCircle className="h-3.5 w-3.5" aria-hidden="true" />}
          onClick={onCancel}
        >
          Cancelar
        </Button>
      )}
    </>
  );
}

function NotificationFormModal({
  open,
  onClose,
  onSubmit,
  blocks,
  prefill,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: NotificationCreateInput) => Promise<void>;
  blocks: HomeBlock[];
  prefill: { targetType: NotificationTargetType; targetId: string; title: string } | null;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [targetType, setTargetType] = useState<NotificationTargetType>("none");
  const [targetId, setTargetId] = useState("");
  const [targetPath, setTargetPath] = useState("/");
  const [schedule, setSchedule] = useState<"now" | "once" | "daily" | "weekdays" | "weekly">("now");
  const [scheduledAt, setScheduledAt] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sendTime, setSendTime] = useState("19:30");
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const routeOptions = [
    ["/", "Inicio"],
    ["/eventos", "Eventos"],
    ["/biblia", "Biblia"],
    ["/podcast", "Podcast"],
    ["/ubicaciones", "Ubicaciones"],
    ["/ministerios", "Ministerios"],
    ["/primeros-pasos", "Primeros pasos"],
    ["/preparar-visita", "Preparar visita"],
    ["/mas", "Mas"],
  ];
  const occurrencePreview = useMemo(
    () => previewOccurrences(schedule, dateFrom, dateTo, sendTime, weekdays),
    [schedule, dateFrom, dateTo, sendTime, weekdays],
  );

  useEffect(() => {
    if (!open || !prefill) return;
    setTargetType(prefill.targetType);
    setTargetId(prefill.targetId);
    setTitle(prefill.title ? `Aviso: ${prefill.title}` : "");
  }, [open, prefill]);

  function reset() {
    setTitle("");
    setBody("");
    setTargetType("none");
    setTargetId("");
    setTargetPath("/");
    setSchedule("now");
    setScheduledAt("");
    setDateFrom("");
    setDateTo("");
    setSendTime("19:30");
    setWeekdays([]);
    setError(null);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        title: title.trim(),
        body: body.trim(),
        target: targetType === "route" ? targetPath : targetType === "home" ? "/" : targetType === "home_block" ? `/contenido/${targetId}` : null,
        targetType,
        targetId: targetType === "home_block" ? targetId : null,
        targetPath: targetType === "home" ? "/" : targetType === "route" ? targetPath : null,
        audience: { type: "all" },
        scheduledAt: schedule === "once" ? fromDatetimeLocalValue(scheduledAt) : null,
        schedule:
          schedule === "now"
            ? null
            : {
                mode: schedule,
                scheduledAt: schedule === "once" ? fromDatetimeLocalValue(scheduledAt) : null,
                dateFrom: schedule === "once" ? null : dateFrom,
                dateTo: schedule === "once" ? null : dateTo,
                time: schedule === "once" ? null : sendTime,
                weekdays,
                timezone: "Europe/Madrid",
              },
      });
      reset();
      onClose();
    } catch {
      setError("No se pudo crear la notificacion. Revisa los datos e intentalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Nueva notificacion"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" form="notification-form" loading={submitting}>
            Crear notificacion
          </Button>
        </>
      }
    >
      <form id="notification-form" onSubmit={handleSubmit} className="space-y-4">
        <FormField label="Titulo" required>
          {(field) => <Input {...field} required value={title} onChange={(e) => setTitle(e.target.value)} disabled={submitting} />}
        </FormField>
        <FormField label="Mensaje" required>
          {(field) => (
            <Textarea {...field} required value={body} onChange={(e) => setBody(e.target.value)} disabled={submitting} />
          )}
        </FormField>
        <FormField label="Destino al pulsar">
          {(field) => (
            <Select
              {...field}
              value={targetType}
              onChange={(e) => setTargetType(e.target.value as NotificationTargetType)}
              disabled={submitting}
            >
              <option value="none">Sin accion</option>
              <option value="home">Inicio</option>
              <option value="home_block">Un bloque concreto</option>
              <option value="route">Una pagina de IESAPP</option>
            </Select>
          )}
        </FormField>

        {targetType === "home_block" && (
          <>
          {targetId && (
            <p className="rounded-lg bg-surface-subtle px-3 py-2 text-sm text-ink-muted">
              Al pulsar: {blocks.find((block) => block.id === targetId)?.label || blocks.find((block) => block.id === targetId)?.title || targetId}
            </p>
          )}
          <FormField label="Bloque">
            {(field) => (
              <Select {...field} required value={targetId} onChange={(e) => setTargetId(e.target.value)} disabled={submitting}>
                <option value="">Selecciona un bloque</option>
                {blocks.map((block) => (
                  <option key={block.id} value={block.id}>
                    {block.label || block.title || block.id}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          </>
        )}

        {targetType === "route" && (
          <FormField label="Pagina">
            {(field) => (
              <Select {...field} value={targetPath} onChange={(e) => setTargetPath(e.target.value)} disabled={submitting}>
                {routeOptions.map(([path, label]) => (
                  <option key={path} value={path}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        )}

        <FormField label="Audiencia inicial">
          {() => (
            <p className="rounded-lg bg-surface-subtle px-3 py-2 text-sm text-ink-muted">
              Todas las personas usuarias.
            </p>
          )}
        </FormField>

        <FormField label="Envio">
          {(field) => (
            <Select
              {...field}
              value={schedule}
              onChange={(e) => setSchedule(e.target.value as typeof schedule)}
              disabled={submitting}
            >
              <option value="now">Inmediato</option>
              <option value="once">Enviar una vez</option>
              <option value="daily">Todos los dias durante un periodo</option>
              <option value="weekdays">Dias especificos durante un periodo</option>
              <option value="weekly">Semanalmente durante un periodo</option>
            </Select>
          )}
        </FormField>

        {schedule === "once" && (
          <FormField label="Fecha y hora de envio" required>
            {(field) => (
              <Input
                {...field}
                type="datetime-local"
                required
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                disabled={submitting}
              />
            )}
          </FormField>
        )}

        {schedule !== "now" && schedule !== "once" && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <FormField label="Desde" required>{(field) => <Input {...field} type="date" required value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />}</FormField>
            <FormField label="Hasta" required>{(field) => <Input {...field} type="date" required value={dateTo} onChange={(e) => setDateTo(e.target.value)} />}</FormField>
            <FormField label="Hora" required>{(field) => <Input {...field} type="time" required value={sendTime} onChange={(e) => setSendTime(e.target.value)} />}</FormField>
          </div>
        )}

        {schedule === "weekdays" && (
          <div className="flex flex-wrap gap-2">
            {["L", "M", "X", "J", "V", "S", "D"].map((label, index) => (
              <Button
                key={label}
                type="button"
                size="sm"
                variant={weekdays.includes(index) ? "primary" : "outline"}
                onClick={() =>
                  setWeekdays((current) =>
                    current.includes(index) ? current.filter((item) => item !== index) : [...current, index].sort(),
                  )
                }
              >
                {label}
              </Button>
            ))}
          </div>
        )}

        {occurrencePreview && (
          <p className="rounded-lg bg-surface-subtle px-3 py-2 text-sm text-ink-muted">
            Se programaran {occurrencePreview.count} envios. Proximos: {occurrencePreview.items.join(", ")}
          </p>
        )}

        {error && (
          <p role="alert" className="rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}
